import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, Star, Trash2, ArrowRight, Save, AlertCircle } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import { toast } from "sonner";

const TAGS = [
  "Headshot", "LinkedIn", "Instagram", "Facebook", "Website", "Blog",
  "Email Signature", "Speaker Bio", "Lifestyle", "Professional", "Casual", "Seasonal", "Other",
];

const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export default function Step5Upload() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const refresh = useCallback(async () => {
    const [g, p] = await Promise.all([api.get("/gallery"), api.get("/prompts")]);
    setItems(g.data.images || []);
    setPrompts(p.data.prompts || []);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validate = (files) => {
    const valid = [];
    for (const f of files) {
      if (!ACCEPT.includes(f.type)) {
        toast.error(`${f.name}: only JPG, PNG, or WEBP allowed`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: file is over 10MB`);
        continue;
      }
      valid.push(f);
    }
    return valid;
  };

  const handleFiles = async (filesIn) => {
    const files = validate(Array.from(filesIn || []));
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} image${files.length === 1 ? "" : "s"} uploaded`);
      await refresh();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const updateItem = async (id, payload) => {
    await api.patch(`/gallery/${id}`, payload);
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...payload } : it)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/gallery/${id}`);
    refresh();
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="step5-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="hairline" />
        <span className="eyebrow">Step 5 of 6</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D]">Step 5: Upload Your AI-Generated Images</h1>
      <p className="text-[#52525b] mt-3 text-lg max-w-3xl leading-relaxed">
        Save your best results here so they're all in one place, organised and ready to download.
      </p>

      {/* Drop zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mt-10 bsp-card p-10 md:p-14 text-center cursor-pointer transition-all duration-200 ${
          dragOver ? "ring-2 ring-[#D4A853] bg-[#F5E6E0]/40" : ""
        }`}
        style={{ border: dragOver ? "2px dashed #D4A853" : "2px dashed #e5e7eb" }}
        data-testid="drop-zone"
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="file-input"
        />
        <div className="w-16 h-16 rounded-2xl bg-[#1A1F3D] flex items-center justify-center mx-auto">
          <Upload className="w-7 h-7 text-[#D4A853]" strokeWidth={1.6} />
        </div>
        <h3 className="font-display text-2xl text-[#1A1F3D] mt-5">
          {uploading ? "Uploading…" : "Drag generated images here, or click to browse"}
        </h3>
        <p className="mt-2 text-sm text-[#52525b]">JPG, PNG, or WEBP · No limit</p>
        <span className="btn-primary mt-6 inline-flex pointer-events-none" aria-hidden>
          <Upload className="w-4 h-4" /> Choose images
        </span>
      </div>

      {/* Uploaded items list */}
      {items.length === 0 ? (
        <div className="mt-8 bsp-card p-6 flex items-center gap-3 text-sm text-[#52525b]" data-testid="empty">
          <AlertCircle className="w-4 h-4 text-[#D4A853]" />
          No images yet — upload at least one to unlock your gallery.
        </div>
      ) : (
        <div className="mt-10 space-y-4" data-testid="items-list">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-2xl text-[#1A1F3D]">Tag your images</h2>
            <span className="text-sm text-[#52525b]">{items.length} uploaded</span>
          </div>
          {items.map((it) => (
            <ItemCard
              key={it.id}
              item={it}
              prompts={prompts}
              onUpdate={(payload) => updateItem(it.id, payload)}
              onDelete={() => remove(it.id)}
            />
          ))}

          <div className="mt-6 bsp-card p-6 md:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#1A1F3D] text-white" data-testid="step5-continue">
            <div>
              <div className="font-display text-2xl">Step 5 saved.</div>
              <div className="text-white/70 text-sm mt-1">Browse, filter, and download in your gallery.</div>
            </div>
            <button
              onClick={() => navigate("/app/step6")}
              className="px-5 py-3 rounded-lg bg-[#D4A853] text-[#1A1F3D] font-semibold inline-flex items-center gap-2 transition-colors hover:bg-[#C29543]"
              data-testid="view-gallery-btn"
            >
              View Your Gallery <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 text-sm text-[#52525b]">
        Need to go back? <Link to="/app/step4" className="gold-link">Step 4: Generate in ChatGPT</Link>
      </div>
    </div>
  );
}

const ItemCard = ({ item, prompts, onUpdate, onDelete }) => {
  const [tags, setTags] = useState(item.tags || []);
  const [promptUsed, setPromptUsed] = useState(item.prompt_used || "");
  const [fav, setFav] = useState(!!item.is_favourite);
  const [src, setSrc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/gallery/${item.id}/file?token=${encodeURIComponent(getToken() || "")}`);
        if (!r.ok) return;
        const b = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      } catch (err) {
        console.error("Failed to load reference photo thumbnail:", err);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id]);

  const toggleTag = (t) => {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const toggleFav = async () => {
    const next = !fav;
    setFav(next);
    await onUpdate({ is_favourite: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate({ tags, prompt_used: promptUsed });
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bsp-card p-5 grid md:grid-cols-12 gap-5" data-testid={`item-${item.id}`}>
      <div className="md:col-span-3">
        <div className="aspect-square rounded-lg overflow-hidden bg-[#F5E6E0]">
          {src && <img src={src} alt={item.file_name} className="w-full h-full object-cover" />}
        </div>
      </div>
      <div className="md:col-span-9 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-[#52525b] mb-2">Tag this image</div>
          <div className="flex flex-wrap gap-2" data-testid={`tags-${item.id}`}>
            {TAGS.map((t) => {
              const active = tags.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? "bg-[#D4A853] border-[#D4A853] text-[#1A1F3D]"
                      : "bg-white border-[#e5e7eb] text-[#52525b] hover:border-[#D4A853]"
                  }`}
                  data-testid={`tag-chip-${item.id}-${t}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-[#52525b]">Which prompt did you use?</label>
          <select
            className="bsp-input mt-1.5"
            value={promptUsed}
            onChange={(e) => setPromptUsed(e.target.value)}
            data-testid={`prompt-select-${item.id}`}
          >
            <option value="">— Optional —</option>
            {prompts.map((p) => (
              <option key={p.id} value={p.title}>{p.category} · {p.title}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <button
            onClick={toggleFav}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              fav ? "bg-[#D4A853] text-[#1A1F3D]" : "bg-[#F5E6E0] text-[#1A1F3D] hover:bg-[#ecd6cd]"
            }`}
            data-testid={`fav-${item.id}`}
          >
            <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} /> {fav ? "Favourite" : "Mark as favourite"}
          </button>
          <div className="flex gap-2">
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 border border-[#e5e7eb] hover:bg-red-50 transition-colors"
              data-testid={`delete-${item.id}`}
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary text-sm"
              data-testid={`save-${item.id}`}
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
